import { ChaimeraEditor } from "@chaimera/editor";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { DesignOpenGate, ProofEditWarning } from "../components/DesignEditPrompt.tsx";
import { LoadingSpinnerWithBackground, useToast } from "../components/legacy/ui.tsx";
import { NameDesignModal } from "../components/NameDesignModal.tsx";
import { type Artwork, api } from "../lib/api.ts";
import { builderClient, listDesigns } from "../lib/artwork-builder.ts";
import { specAudience, useActiveAusPostSpec } from "../lib/auspost-spec.ts";
import { useProfile } from "./account.tsx";

/**
 * DIY Design Tool for a design that belongs to no campaign yet. Exporting a
 * PDF adds it to the signed-in user's artwork library.
 */
export const Route = createFileRoute("/account/design/$designId")({
  component: AccountDesignTool,
  validateSearch: (raw: Record<string, unknown>): { from?: "templates"; scope?: "ready" } => ({
    ...(raw.from === "templates" ? { from: "templates" as const } : {}),
    ...(raw.scope === "ready" ? { scope: "ready" as const } : {}),
  }),
});

function AccountDesignTool() {
  const { designId } = Route.useParams();
  const { from, scope } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const profile = useProfile().data?.profile;
  const owner = profile?.id;
  const ausPost = useActiveAusPostSpec(null);
  const client = useMemo(() => (owner ? builderClient(owner) : null), [owner]);
  const designs = useQuery({
    queryKey: ["designs", owner],
    enabled: Boolean(client),
    queryFn: () => listDesigns(owner!),
  });
  const known = designs.data?.find((d) => d.id === designId);
  const nameIt = useMutation({
    mutationFn: (name: string) => client!.renameArtwork(designId, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["designs", owner] }),
    onError: (e: Error) => showToast(e.message || "Could not name the design", "error"),
  });

  const back = () => {
    queryClient.invalidateQueries({ queryKey: ["designs"] });
    if (from === "templates") {
      queryClient.invalidateQueries({ queryKey: ["design-templates"] });
    }
    navigate({
      to: "/account/artwork",
      search: from === "templates" ? { view: "templates" } : {},
    });
  };

  if (!client || !designs.data || (designs.isFetching && !known) || ausPost.isPending) {
    return <LoadingSpinnerWithBackground />;
  }
  if (ausPost.isError || !ausPost.data) {
    return <p className="p-6 text-sm text-gray-700">AusPost spec is not loaded.</p>;
  }
  if (known?.name === "") {
    return (
      <NameDesignModal
        pending={nameIt.isPending}
        onCancel={back}
        onSave={(name) => nameIt.mutate(name)}
      />
    );
  }
  return (
    <DesignOpenGate designId={designId} scope={scope} from={from} onLeave={back}>
      {({ warnProof, onSaved }) => (
        <div className="fixed inset-0 z-50 flex flex-col bg-white">
          <ProofEditWarning show={warnProof} />
          <div className="min-h-0 flex-1">
            <ChaimeraEditor
              className="h-full"
              client={client}
              artworkId={designId}
              ausPostSpec={ausPost.data.spec}
              specRules={[]}
              specAudience={specAudience(profile?.role)}
              logoSrc="/logo-96-96.png"
              onBack={back}
              onSaved={onSaved}
              onExported={async (rec) => {
                // PNG/JPG are proofs for the user; only the PDF is print artwork.
                if (rec.format !== "pdf") return undefined;
                try {
                  await api.post<{ artwork: Artwork }>("/artwork/from-design", {
                    exportId: rec.id,
                  });
                  queryClient.invalidateQueries({ queryKey: ["artwork"] });
                  showToast("Design added to your artwork", "success");
                  back();
                } catch (e) {
                  showToast((e as Error).message || "Could not add the design", "error");
                }
                return false;
              }}
            />
          </div>
        </div>
      )}
    </DesignOpenGate>
  );
}
